import {Alert, Checkbox, Stack} from '@mantine/core';
import React, {useCallback} from 'react';

import {AntibodyEnforcedNamingStrategySpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {getShouldAddNamingStrategies} from 'antibodies/schema-configuration/schema-migration/schemaMigrationNamingStrategies';
import {LABELING_STRATEGY_TO_TEXT, RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {NamingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import getTestProps from 'test/lib/cypress/getTestProps';

import MigrationPreviewSection from './MigrationPreviewSection';

export interface NamingStrategyMigrationPreviewProps {
  enforcedNamingStrategySpec: AntibodyEnforcedNamingStrategySpec;
  existingNamingStrategies: Array<RegistryLabelingStrategy> | null;
  registryEnabledNamingStrategies: Array<NamingStrategy>;
  selectableNamingStrategies: Array<NamingStrategy>;
  selectedNamingStrategies: Array<NamingStrategy>;
  onSelectedNamingStrategiesChange: (strategies: Array<NamingStrategy>) => void;
}

export default function NamingStrategyMigrationPreview({
  enforcedNamingStrategySpec,
  existingNamingStrategies,
  registryEnabledNamingStrategies,
  selectableNamingStrategies,
  selectedNamingStrategies,
  onSelectedNamingStrategiesChange,
}: NamingStrategyMigrationPreviewProps): JSX.Element {
  const shouldAddNamingStrategies = getShouldAddNamingStrategies(
    enforcedNamingStrategySpec,
    existingNamingStrategies
  );

  const handleStrategyToggle = useCallback(
    (strategy: NamingStrategy): void => {
      if (selectedNamingStrategies.includes(strategy)) {
        onSelectedNamingStrategiesChange(
          selectedNamingStrategies.filter((selectedStrategy) => selectedStrategy !== strategy)
        );
      } else {
        onSelectedNamingStrategiesChange([...selectedNamingStrategies, strategy]);
      }
    },
    [onSelectedNamingStrategiesChange, selectedNamingStrategies]
  );

  if (!shouldAddNamingStrategies) {
    return (
      <MigrationPreviewSection title="Labeling strategies" description="No changes to labeling strategies." />
    );
  }

  const noneSelected = selectedNamingStrategies.length === 0;

  return (
    <MigrationPreviewSection
      title="Labeling strategies"
      description="Select at least one labeling strategy to add to the schema."
    >
      <Stack spacing="xs">
        <Stack
          spacing="xs"
          {...getTestProps({component: 'NamingStrategyMigrationPreview', element: 'strategies'})}
        >
          {registryEnabledNamingStrategies.map((strategy) => {
            const isEnabled = selectableNamingStrategies.includes(strategy);
            return (
              <Checkbox
                key={strategy}
                checked={isEnabled && selectedNamingStrategies.includes(strategy)}
                disabled={!isEnabled}
                label={
                  LABELING_STRATEGY_TO_TEXT[
                    RegistryLabelingStrategy[strategy as keyof typeof RegistryLabelingStrategy]
                  ]
                }
                onChange={() => {
                  handleStrategyToggle(strategy);
                }}
                value={strategy}
              />
            );
          })}
        </Stack>
        {noneSelected && (
          <Alert variant="warning">Select at least one labeling strategy to apply the migration.</Alert>
        )}
      </Stack>
    </MigrationPreviewSection>
  );
}
